import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Sparkles } from "lucide-react";
import { PageShell } from "@/components/sections";
import { VENDORS_BY_CATEGORY } from "@/lib/vendorData";
import { requireMember } from "@/lib/auth";
import { devLog } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TO_CATEGORY = {
  djs: "djs",
  dj: "djs",
  production: "production",
  "content creation": "content-creation",
  catering: "catering",
  "event planner": "event-planner",
  "videography/photography": "video-photo",
  "video & photo": "video-photo",
  photography: "video-photo",
  decor: "decor",
  florist: "decor",
  "hair & makeup": "hair-makeup",
  "hair & make-up": "hair-makeup",
  outfits: "bride-101",
  "bride 101": "bride-101",
  "groom 101": "groom-101",
  entertainment: "djs",
  venue: "venue",
};

const vendorMatchesCategory = (vendor, categorySlug) =>
  (vendor.service_types || [])
    .map((s) => (s || "").toLowerCase().trim())
    .some((s) => SERVICE_TO_CATEGORY[s] === categorySlug);

/* Single source of truth for category data.
   Keep ids in sync with EXPLORE_VENDORS in VendorListing.jsx */
const CATEGORIES = {
  djs: {
    title: "DJs",
    image: "/categories/djs.jpg",
    tagline: "Sound, mood & momentum.",
    blurb:
      "From sundowner sets to dancefloor finales, our curated DJs craft the soundtrack of your day with taste, technique and timing.",
  },
  production: {
    title: "Production",
    image: "/categories/production.jpg",
    tagline: "The architecture of an unforgettable night.",
    blurb:
      "Lighting, staging, sound and rigging — production specialists who turn raw venues into immersive, cinematic moments.",
  },
  "content-creation": {
    title: "Content Creation",
    image: "/categories/content.jpg",
    tagline: "Capture it. Share it. Live it twice.",
    blurb:
      "Reels, behind-the-scenes and same-day edits from creators who understand the language of weddings and social.",
  },
  catering: {
    title: "Catering",
    image: "/categories/catering.jpg",
    tagline: "A menu worth lingering over.",
    blurb:
      "Plated dinners, grazing tables and global flavours from caterers who treat hospitality as an art form.",
  },
  "event-planner": {
    title: "Event Planner",
    image: "/categories/event-planner.jpg",
    tagline: "Craft your perfect event.",
    blurb:
      "Full-service planners and stylists who orchestrate every detail — from concept and timeline to flawless on-the-day execution.",
  },
  "bride-101": {
    title: "Bride 101",
    image: "/categories/bride-101-v2.jpg",
    tagline: "Everything for the bride, in one place.",
    blurb:
      "Dresses, fittings, beauty trials, bridal suites and the small luxuries — a curated edit for the bride-to-be.",
  },
  "hair-makeup": {
    title: "Hair & Makeup",
    image: "/categories/hair-makeup-v2.jpg",
    tagline: "Enhance your natural beauty.",
    blurb:
      "Editorial-grade artists who tailor every look to your features, your dress, and the light of the day.",
  },
  "video-photo": {
    title: "Videography & Photography",
    image: "/categories/video-photo-v3.jpg",
    tagline: "Capture every moment.",
    blurb:
      "Photographers and filmmakers with a documentary eye — capturing the looks, the laughter, the in-between.",
  },
  decor: {
    title: "Wedding Decor",
    image: "/categories/wedding-decor-v2.jpg",
    tagline: "Atmosphere, considered.",
    blurb:
      "Florals, tablescapes, installations and signage — designers who layer texture, palette and scale into one cohesive world.",
  },
  "groom-101": {
    title: "Groom 101",
    image: "/categories/groom-101-v2.jpg",
    tagline: "Own the big day.",
    blurb:
      "Suits, grooming, gifting and groomsmen logistics — the essentials for the groom and his party.",
  },
  "wedding-cakes": {
    title: "Wedding Cakes",
    image: "/categories/wedding-cakes.jpg",
    tagline: "The sweetest centrepiece.",
    blurb:
      "Cake artists crafting showstopping tiers and intimate bakes — flavours and finishes designed around your day.",
  },
  "wedding-stationery": {
    title: "Wedding Stationery",
    image: "/categories/wedding-stationery.jpg",
    tagline: "Set the tone on paper.",
    blurb:
      "Invitations, signage and keepsakes — stationers who carry your wedding's identity through every printed detail.",
  },
  photobooth: {
    title: "PhotoBooth",
    image: "/categories/photobooth.jpg",
    tagline: "Moments, captured playfully.",
    blurb:
      "Booths, backdrops and instant keepsakes that keep guests entertained and memories in hand.",
  },
};

const VendorTile = ({ vendor }) => (
  <Link
    to={`/vendors/${vendor.slug}`}
    data-testid={`vendor-tile-${vendor.slug}`}
    aria-label={`Open ${vendor.name} profile`}
    className="group block w-full focus:outline-none"
  >
    <div className="explore-tile relative w-full overflow-hidden focus:ring-2 focus:ring-[var(--ld-gold)] focus:ring-offset-4 focus:ring-offset-[var(--ld-ivory)]">
      <img
        src={vendor.tileImage || vendor.image}
        alt={vendor.name}
        loading="lazy"
        className="block w-full h-auto aspect-square object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
      />
      {/* Permanent inner vignette — matches Explore Vendors tiles */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 explore-tile-vignette"
      />
      {/* Hover gold wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 explore-tile-gold-wash"
      />
      {/* Hover gold ring */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 ring-1 ring-transparent group-hover:ring-[var(--ld-gold)]/55 transition-colors duration-500"
      />
      <div className="absolute top-3 right-3 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        <span className="inline-flex h-9 w-9 items-center justify-center bg-[var(--ld-ivory)]/95 text-[var(--ld-ink)]">
          <ArrowUpRight size={14} strokeWidth={1.6} />
        </span>
      </div>
    </div>
    <p
      data-testid={`vendor-tile-name-${vendor.slug}`}
      className="mt-4 text-center font-serif-display text-xl md:text-2xl font-bold tracking-wide text-[var(--ld-gold-deep)] group-hover:text-[var(--ld-gold)] transition-colors"
    >
      {vendor.tileName || vendor.name}
    </p>
  </Link>
);

const DbVendorTile = ({ vendor }) => {
  const cover =
    (vendor.media_urls || []).find(
      (u) => !/\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(u)
    ) || null;
  const href = vendor.slug ? `/vendors/${vendor.slug}` : "/vendor-listing";
  return (
    <Link
      to={href}
      data-testid={`vendor-tile-db-${vendor.id}`}
      aria-label={`Open ${vendor.company_name} profile`}
      className="explore-tile group relative block w-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-[var(--ld-gold)] focus:ring-offset-4 focus:ring-offset-[var(--ld-ivory)]"
    >
      <div className="aspect-square w-full bg-[var(--ld-ink)]/10">
        {cover ? (
          <img
            src={cover}
            alt={vendor.company_name}
            loading="lazy"
            className="block w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[var(--ld-gold-deep)]">
            <Sparkles size={28} strokeWidth={1.4} />
          </div>
        )}
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 explore-tile-vignette"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 explore-tile-gold-wash"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 ring-1 ring-transparent group-hover:ring-[var(--ld-gold)]/55 transition-colors duration-500"
      />
      <div className="absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-black/75 to-transparent text-white">
        <p className="font-serif-display text-lg leading-tight">
          {vendor.company_name}
        </p>
      </div>
      <span className="sr-only">{vendor.company_name}</span>
    </Link>
  );
};

const Category = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = CATEGORIES[slug];
  const [dbVendors, setDbVendors] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);

  // Member-only — sign-in + form-completion gate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { user, redirect } = await requireMember();
      if (cancelled) return;
      if (!user) {
        navigate(`/signin?next=/category/${slug}`, { replace: true });
        return;
      }
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }
      setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, slug]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/vendors`);
        if (!cancelled) {
          setDbVendors(
            (r.data || []).filter(
              (v) => v.slug && vendorMatchesCategory(v, slug)
            )
          );
        }
      } catch (e) {
        devLog("ld_category fetch failed:", e?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, authChecked]);

  // Fire-and-forget impression events for every vendor shown in this category.
  // Backend dedupes per visitor+slug within a 30-min window. Must run before
  // any conditional early-return to respect React rules of hooks. useRef
  // guards against React StrictMode firing the effect twice in dev.
  const impressionTrackedRef = useRef(new Set());
  useEffect(() => {
    if (!data) return;
    const hardcoded = VENDORS_BY_CATEGORY[slug] || [];
    const legacySlugs = new Set(hardcoded.map((v) => v.slug));
    // Dedupe — legacy wins, so impression tracking matches what's rendered.
    const slugs = [
      ...hardcoded.map((v) => v.slug),
      ...dbVendors.filter((v) => v.slug && !legacySlugs.has(v.slug)).map((v) => v.slug),
    ];
    slugs.forEach((s) => {
      const key = `${slug}:${s}`;
      if (impressionTrackedRef.current.has(key)) return;
      impressionTrackedRef.current.add(key);
      axios
        .post(`${API}/metrics/event`, { vendor_slug: s, kind: "impression" })
        .catch(() => {});
    });
  }, [slug, data, dbVendors]);

  if (!data) {
    return <Navigate to="/vendor-listing" replace />;
  }

  if (!authChecked) {
    return (
      <PageShell testId={`category-page-${slug}-loading`}>
        <div className="px-6 md:px-12 py-24 text-center text-[var(--ld-ink-muted)] text-sm">
          Loading…
        </div>
      </PageShell>
    );
  }

  const hardcoded = VENDORS_BY_CATEGORY[slug] || [];
  /* Dedupe: when a hardcoded legacy vendor and a DB vendor share the same
   * slug, the LEGACY entry wins (it has the richer gold typography +
   * curated gallery photos). The DB row exists purely for admin editing
   * from the Vendor Form Data tab and shouldn't double up on the public
   * /category/* pages.
   */
  const legacySlugSet = new Set(hardcoded.map((v) => v.slug));
  const filteredDb = dbVendors.filter((v) => !legacySlugSet.has(v.slug));
  const totalCount = hardcoded.length + filteredDb.length;

  return (
    <PageShell testId={`category-page-${slug}`}>
      {/* Hero */}
      <section data-testid="category-hero" className="relative w-full">
        <div
          className={
            "relative h-[58vh] min-h-[420px] w-full overflow-hidden " +
            (data.bannerFit === "contain" ? "bg-[var(--ld-ink)]" : "")
          }
        >
          <img
            src={data.image}
            alt={data.title}
            className={
              "absolute inset-0 h-full w-full " +
              (data.bannerFit === "contain" ? "object-contain" : "object-cover")
            }
          />
          {data.bannerFit !== "contain" && (
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20"
            />
          )}
          {/* Always-visible back link in top-left */}
          <div className="absolute top-6 left-6 md:top-8 md:left-12 z-20">
            <Link
              to="/vendor-listing"
              data-testid="category-back-link"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold)] hover:text-white transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.6} />
              All Categories
            </Link>
          </div>
          {/* Title + tagline overlay (hidden when image already has title baked in) */}
          {!data.hideTitleOverlay && (
            <div className="relative z-10 h-full flex items-end">
              <div className="mx-auto w-full max-w-7xl px-6 md:px-12 pb-12 md:pb-16">
                <h1
                  data-testid="category-title"
                  className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-white font-medium leading-[1.05]"
                >
                  {data.title}
                </h1>
                <p className="mt-3 max-w-xl text-[15px] md:text-base text-white/80 italic font-serif-display">
                  {data.tagline}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {totalCount > 0 ? (
        /* Vendor grid */
        <section
          data-testid="category-vendors"
          className="relative px-6 md:px-12 py-16 md:py-24"
        >
          <div className="mx-auto max-w-7xl">
            <div className="text-center max-w-2xl mx-auto">
              <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                Featured
              </span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight">
                {data.title} we love
              </h2>
              <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)]">
                Tap a vendor to view their profile.
              </p>
              <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
            </div>

            <ul
              data-testid="category-vendor-grid"
              className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {hardcoded.map((v) => (
                <li key={v.slug}>
                  <VendorTile vendor={v} />
                </li>
              ))}
              {filteredDb.map((v) => (
                <li key={v.id}>
                  <DbVendorTile vendor={v} />
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : (
        /* Coming soon body */
        <section className="relative px-6 md:px-12 py-20 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center justify-center h-12 w-12 bg-[var(--ld-ink)] text-[var(--ld-gold)]">
              <Sparkles size={18} strokeWidth={1.5} />
            </span>

            <p className="mt-8 font-accent text-[11px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)]">
              Coming Soon
            </p>
            <h2
              data-testid="category-coming-soon"
              className="mt-4 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight"
            >
              {data.title} vendors are being curated.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-[var(--ld-ink-muted)]">
              {data.blurb}
            </p>
            <p className="mt-3 text-[14px] text-[var(--ld-ink-muted)]">
              We&apos;re hand-picking the finest names in this category. Check back
              soon.
            </p>

            <div className="mx-auto mt-10 h-px w-20 bg-[var(--ld-gold)]/60" />

            <div className="mt-10 flex justify-center">
              <Link
                to="/vendor-listing"
                data-testid="category-cta-back"
                className="inline-flex items-center justify-center gap-2 px-9 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/80 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors duration-300"
              >
                Browse other categories
              </Link>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
};

export default Category;
